import React, { useState } from 'react';
import { Modal, Button, Typography, Alert, message, Spin, Upload, Card, List, Row, Col, Input, InputNumber, DatePicker } from 'antd';
import { InboxOutlined, EyeOutlined, DeleteOutlined, EditOutlined, CloseOutlined, PlusOutlined, MinusCircleOutlined, RobotOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { Text, Title } = Typography;
const { Dragger } = Upload;

const CustomerPOUpload = ({ onDataExtracted, onClose }) => {
  const [fileList, setFileList] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [customerAdded, setCustomerAdded] = useState(false);
  const [productsAdded, setProductsAdded] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [addingProducts, setAddingProducts] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // File handling functions
  const handleFileChange = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    const isValidType = allowedTypes.includes(file.type);
    if (!isValidType) {
      message.error('Please upload PDF, Word, Excel, or image files only.');
      return;
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB.');
      return;
    }
    
    setFileList(prev => [...prev, file]);
  };

  const handleFileRemove = (file) => {
    setFileList(prev => prev.filter(item => item.uid !== file.uid));
  };

  const handleFilePreview = async (file) => {
    if (file.type === 'application/pdf' || file.type.includes('word') || file.type.includes('excel')) {
      message.info('Preview is not available for this file type. The file will be processed when you analyze.');
      return;
    }
    
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj || file);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleAnalyzePO = async () => {
    if (fileList.length === 0) {
      Modal.warning({
        title: 'No files selected',
        content: 'Please upload at least one file to analyze.',
      });
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      fileList.forEach((file) => {
        formData.append('files', file.originFileObj || file);
      });

      const response = await axios.post('/sales-order/analyze-customer-po', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Normalize products to ensure MRP field is set
      const normalizedProducts = (response.data.products || []).map(product => ({
        ...product,
        mrp: product.mrp || product.price || 0, // Use MRP if available, fallback to price
        price: product.price || product.mrp || 0 // Keep price for backward compatibility
      }));
      
      setExtractedData({
        ...response.data,
        products: normalizedProducts
      });
      setEditableData({
        customer: { ...response.data.customer },
        orderDate: response.data.orderDate,
        orderNumber: response.data.orderNumber,
        products: normalizedProducts
      });
    } catch (error) {
      console.error('Error analyzing customer PO:', error);
      setError(error.response?.data?.message || 'Failed to analyze customer PO. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!extractedData?.newCustomers?.length) return;
    
    setAddingCustomer(true);
    try {
      const customer = extractedData.newCustomers[0];
      const response = await axios.post('/customer/', {
        name: customer.name,
        phone: customer.phone || '',
        address: customer.address || ''
      });
      
      setExtractedData(prev => ({
        ...prev,
        existingCustomer: response.data,
        newCustomers: []
      }));
      setCustomerAdded(true);
      message.success('Customer added successfully!');
    } catch (error) {
      console.error('Error adding customer:', error);
      message.error('Failed to add customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleAddProducts = async () => {
    if (!extractedData?.newProducts?.length) return;
    
    setAddingProducts(true);
    try {
      const publisherMap = new Map();
      
      // Create publishers first
      if (extractedData.newPublishers?.length) {
        for (const publisher of extractedData.newPublishers) {
          try {
            const response = await axios.post('/book-publisher/', {
              name: publisher.name
            });
            publisherMap.set(publisher.name, response.data.id);
          } catch (error) {
            console.error('Error creating publisher:', error);
          }
        }
      }
      
      // Create products
      const createdProducts = [];
      for (const product of extractedData.newProducts) {
        try {
          let publisherId = product.publisherId;
          if (!publisherId && product.publisher) {
            publisherId = publisherMap.get(product.publisher);
            
            // Try to find existing publisher
            if (!publisherId) {
              try {
                const existingPublishers = await axios.get('/book-publisher/?query=all');
                const existingPublisher = existingPublishers.data.find(p => 
                  p.name.toLowerCase() === product.publisher.toLowerCase()
                );
                if (existingPublisher) {
                  publisherId = existingPublisher.id;
                } else {
                  // Create new publisher
                  const newPublisher = await axios.post('/book-publisher/', {
                    name: product.publisher
                  });
                  publisherId = newPublisher.data.id;
                }
              } catch (error) {
                console.error('Error handling publisher:', error);
              }
            }
          }

          // Generate temporary ISBN if not available
          let isbn = product.isbn;
          if (!isbn || !isbn.trim()) {
            // Generate temporary ISBN
            const tempNum = createdProducts.length + 1;
            isbn = `NA${tempNum}`;
          }

          // Create FormData for product creation
          const formData = new FormData();
          formData.append("isbn", isbn);
          formData.append("name", product.title || product.name || '');
          formData.append("author", product.author || '');
          if (publisherId) {
            formData.append("book_publisher_id", publisherId);
          }
          formData.append("product_category_id", 7); // Default category
          formData.append("unit_type", 'copy');
          formData.append("unit_measurement", 1);
          formData.append("product_currency_id", 7); // Default currency
          formData.append("purchase_price", 0);
          formData.append("sale_price", product.price || 0);
          formData.append("quantity", 0);
          formData.append("image", 'img');
          formData.append("reorder_quantity", 1);

          const response = await axios.post('/product/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          createdProducts.push({
            ...response.data,
            extractedQuantity: product.quantity,
            extractedPrice: product.price,
            extractedDiscount: product.discount,
            publisherId: publisherId,
            publisherName: product.publisher
          });
        } catch (error) {
          console.error('Error creating product:', error);
        }
      }
      
      setExtractedData(prev => ({
        ...prev,
        existingProducts: [...(prev.existingProducts || []), ...createdProducts],
        newProducts: [],
        newPublishers: []
      }));
      setProductsAdded(true);
      message.success(`${createdProducts.length} products added successfully!`);
    } catch (error) {
      console.error('Error adding products:', error);
      message.error('Failed to add products');
    } finally {
      setAddingProducts(false);
    }
  };

  const handleUseExtractedData = () => {
    const orderData = {
      existingCustomer: extractedData.existingCustomer,
      customer: extractedData.customer,
      orderDate: extractedData.orderDate,
      orderNumber: extractedData.orderNumber,
      existingProducts: extractedData.existingProducts || [],
      newProducts: extractedData.newProducts || [],
      newCustomers: extractedData.newCustomers || [],
      newPublishers: extractedData.newPublishers || []
    };
    
    onDataExtracted(orderData);
  };

  const handleRetry = () => {
    setExtractedData(null);
    setEditableData(null);
    setError(null);
    setAnalysisComplete(false);
    setFileList([]);
  };

  // Functions to handle editing
  const handleEditCustomer = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        [field]: value
      }
    }));
  };

  const handleEditOrderInfo = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditProduct = (index, field, value) => {
    setEditableData(prev => ({
      ...prev,
      products: prev.products.map((product, i) => 
        i === index ? { ...product, [field]: value } : product
      )
    }));
  };

  const handleAddProduct = () => {
    setEditableData(prev => ({
      ...prev,
      products: [...prev.products, {
        isbn: '',
        title: '',
        name: '',
        author: '',
        publisher: '',
        quantity: 1,
        mrp: 0, // Maximum Retail Price (before discount)
        price: 0, // Keep for backward compatibility
        currency: '₹',
        discount: 0
      }]
    }));
  };

  const handleRemoveProduct = (index) => {
    setEditableData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEdits = () => {
    setExtractedData(prev => ({
      ...prev,
      customer: editableData.customer,
      orderDate: editableData.orderDate,
      orderNumber: editableData.orderNumber,
      products: editableData.products
    }));
    setIsEditing(false);
    setAnalysisComplete(false);
    message.success('Changes saved successfully! You can now proceed to analysis again.');
  };

  const handleCancelEdits = () => {
    setEditableData({
      customer: { ...extractedData.customer },
      orderDate: extractedData.orderDate,
      orderNumber: extractedData.orderNumber,
      products: [...(extractedData.products || [])]
    });
    setIsEditing(false);
  };

  const handleProceedToAnalysis = async () => {
    const updatedData = {
      ...extractedData,
      customer: editableData.customer,
      orderDate: editableData.orderDate,
      orderNumber: editableData.orderNumber,
      products: editableData.products
    };
    
    setExtractedData(updatedData);
    
    try {
      const response = await axios.post('/sales-order/analyze-new-items', {
        customer: updatedData.customer,
        products: updatedData.products
      });
      
      // Normalize products to ensure MRP field is set
      const normalizeProduct = (product) => ({
        ...product,
        mrp: product.mrp || product.price || 0,
        price: product.price || product.mrp || 0
      });
      
      setExtractedData(prev => ({
        ...prev,
        existingCustomer: response.data.existingCustomer,
        newCustomers: response.data.newCustomers || [],
        existingProducts: (response.data.existingProducts || []).map(normalizeProduct),
        newProducts: (response.data.newProducts || []).map(normalizeProduct),
        newPublishers: response.data.newPublishers || []
      }));
      
      setAnalysisComplete(true);
      message.success('Analysis complete! Review new items to be added.');
    } catch (error) {
      console.error('Error analyzing new items:', error);
      message.error('Failed to analyze new items. You can still proceed.');
      setAnalysisComplete(true);
    }
  };

  const uploadProps = {
    name: 'files',
    multiple: true,
    fileList,
    accept: '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx',
    beforeUpload: (file) => {
      handleFileChange(file);
      return false;
    },
    onRemove: handleFileRemove,
    onPreview: handleFilePreview,
  };

  return (
    <Modal
      title={
        <span>
          <RobotOutlined style={{ marginRight: 8 }} />
          AI Customer PO Analysis - Upload Files
        </span>
      }
      open={true}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose={true}
      maskClosable={false}
      keyboard={false}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px' }}
    >
      <div style={{ padding: '20px 0' }}>
        {!extractedData && (
          <>
            <Card title="Upload Customer PO Files" style={{ marginBottom: 16 }}>
              <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag files to this area to upload</p>
                <p className="ant-upload-hint">
                  Support PDF, Word (.doc, .docx), Excel (.xls, .xlsx), and image files (JPG, PNG, GIF, BMP, WEBP). Maximum 10MB per file.
                </p>
              </Dragger>

              {fileList.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>Uploaded Files ({fileList.length}):</Text>
                  <List
                    size="small"
                    dataSource={fileList}
                    renderItem={(file) => {
                      const actions = [];
                      
                      if (file.type.startsWith('image/')) {
                        actions.push(
                          <Button
                            key="preview"
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleFilePreview(file)}
                          >
                            Preview
                          </Button>
                        );
                      }
                      
                      actions.push(
                        <Button
                          key="remove"
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleFileRemove(file)}
                        >
                          Remove
                        </Button>
                      );
                      
                      return (
                        <List.Item actions={actions}>
                          <Text>{file.name}</Text>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              )}
            </Card>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button
                type="primary"
                size="large"
                loading={analyzing}
                onClick={handleAnalyzePO}
                disabled={fileList.length === 0}
                icon={<RobotOutlined />}
              >
                {analyzing ? 'Analyzing Customer PO...' : 'Extract PO Data'}
              </Button>
            </div>

            {analyzing && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>AI is analyzing your customer PO...</Text>
                  <br />
                  <Text type="secondary">This may take a few moments</Text>
                </div>
              </div>
            )}

            {error && (
              <Alert
                message="Analysis Failed"
                description={error}
                type="error"
                showIcon
                action={
                  <Button size="small" onClick={handleRetry}>
                    Retry
                  </Button>
                }
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}

        {extractedData && (
          <>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Extracted Data</span>
                  <Button
                    type={isEditing ? "default" : "primary"}
                    icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
                    onClick={() => {
                      if (!isEditing) {
                        setAnalysisComplete(false);
                        setExtractedData(prev => ({
                          ...prev,
                          existingCustomer: null,
                          newCustomers: [],
                          existingProducts: [],
                          newProducts: [],
                          newPublishers: []
                        }));
                      }
                      setIsEditing(!isEditing);
                    }}
                    size="small"
                  >
                    {isEditing ? 'Cancel Edit' : 'Edit Data'}
                  </Button>
                </div>
              }
              style={{ marginTop: 16 }}
            >
              <div style={{ marginBottom: 16 }}>
                <Alert
                  message="Customer PO Analysis Complete"
                  description={isEditing ? "Edit the extracted data below and save your changes." : "Please review the extracted data below and confirm if it's correct."}
                  type="success"
                  showIcon
                />
              </div>

              {/* Customer Information */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Customer Information</Title>
                {isEditing ? (
                  <Row gutter={16}>
                    <Col span={8}>
                      <Input
                        placeholder="Customer name"
                        value={editableData?.customer?.name || ''}
                        onChange={(e) => handleEditCustomer('name', e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                    </Col>
                    <Col span={8}>
                      <Input
                        placeholder="Phone number"
                        value={editableData?.customer?.phone || ''}
                        onChange={(e) => handleEditCustomer('phone', e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                    </Col>
                    <Col span={8}>
                      <Input
                        placeholder="Address"
                        value={editableData?.customer?.address || ''}
                        onChange={(e) => handleEditCustomer('address', e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                    </Col>
                  </Row>
                ) : (
                  <div>
                    <p><strong>Name:</strong> {extractedData.customer?.name || 'Not found'}</p>
                    <p><strong>Phone:</strong> {extractedData.customer?.phone || 'Not found'}</p>
                    <p><strong>Address:</strong> {extractedData.customer?.address || 'Not found'}</p>
                  </div>
                )}
              </div>

              {/* Order Information */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Order Information</Title>
                {isEditing ? (
                  <Row gutter={16}>
                    <Col span={12}>
                      <DatePicker
                        value={editableData?.orderDate ? moment(editableData.orderDate) : null}
                        onChange={(date) => handleEditOrderInfo('orderDate', date ? date.format('YYYY-MM-DD') : '')}
                        style={{ width: '100%', marginBottom: 8 }}
                        placeholder="Select order date"
                      />
                    </Col>
                    <Col span={12}>
                      <Input
                        placeholder="Order number"
                        value={editableData?.orderNumber || ''}
                        onChange={(e) => handleEditOrderInfo('orderNumber', e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                    </Col>
                  </Row>
                ) : (
                  <div>
                    <p><strong>Order Date:</strong> {extractedData.orderDate || 'Not found'}</p>
                    <p><strong>Order Number:</strong> {extractedData.orderNumber || 'Not found'}</p>
                  </div>
                )}
              </div>

              {/* Products */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Title level={5}>Products ({editableData?.products?.length || extractedData.products?.length || 0})</Title>
                  {isEditing && (
                    <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddProduct}>
                      Add Product
                    </Button>
                  )}
                </div>
                <List
                  dataSource={isEditing ? editableData.products : extractedData.products}
                  renderItem={(product, index) => (
                    <List.Item>
                      {isEditing ? (
                        <Row gutter={8} style={{ width: '100%' }}>
                          <Col span={4}>
                            <Input
                              placeholder="ISBN"
                              value={product.isbn || ''}
                              onChange={(e) => handleEditProduct(index, 'isbn', e.target.value)}
                              size="small"
                            />
                          </Col>
                          <Col span={5}>
                            <Input
                              placeholder="Title"
                              value={product.title || product.name || ''}
                              onChange={(e) => {
                                handleEditProduct(index, 'title', e.target.value);
                                handleEditProduct(index, 'name', e.target.value);
                              }}
                              size="small"
                            />
                          </Col>
                          <Col span={4}>
                            <Input
                              placeholder="Author"
                              value={product.author || ''}
                              onChange={(e) => handleEditProduct(index, 'author', e.target.value)}
                              size="small"
                            />
                          </Col>
                          <Col span={2}>
                            <InputNumber
                              placeholder="Qty"
                              value={product.quantity || 1}
                              onChange={(value) => handleEditProduct(index, 'quantity', value)}
                              min={1}
                              style={{ width: '100%' }}
                              size="small"
                            />
                          </Col>
                          <Col span={3}>
                            <InputNumber
                              placeholder="MRP"
                              value={product.mrp || product.price || 0}
                              onChange={(value) => {
                                handleEditProduct(index, 'mrp', value);
                                handleEditProduct(index, 'price', value); // Keep for backward compatibility
                              }}
                              min={0}
                              style={{ width: '100%' }}
                              size="small"
                            />
                          </Col>
                          <Col span={2}>
                            <InputNumber
                              placeholder="Disc %"
                              value={product.discount || 0}
                              onChange={(value) => handleEditProduct(index, 'discount', value)}
                              min={0}
                              max={100}
                              style={{ width: '100%' }}
                              size="small"
                            />
                          </Col>
                          <Col span={2}>
                            <Button
                              type="link"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => handleRemoveProduct(index)}
                              size="small"
                            />
                          </Col>
                        </Row>
                      ) : (
                        <div style={{ width: '100%' }}>
                          <Text strong>{product.title || product.name || 'Unknown'}</Text>
                          <br />
                          <Text type="secondary">
                            ISBN: {product.isbn || 'N/A'} | 
                            Author: {product.author || 'N/A'} | 
                            Publisher: {product.publisher || 'N/A'}
                            <br />
                            Qty: {product.quantity || 'N/A'} | 
                            MRP: ₹{product.mrp || product.price || 'N/A'} | 
                            Discount: {product.discount || 0}% | 
                            Final Price: ₹{product.mrp && product.discount ? ((product.mrp || product.price || 0) * (1 - (product.discount || 0) / 100)).toFixed(2) : (product.discountedPrice || product.price || 'N/A')}
                          </Text>
                        </div>
                      )}
                    </List.Item>
                  )}
                />
              </div>

              {isEditing && (
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <Button onClick={handleCancelEdits} style={{ marginRight: 8 }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSaveEdits}>
                    Save Changes
                  </Button>
                </div>
              )}
            </Card>

            {/* Analysis Results */}
            {analysisComplete && (
              <>
                {extractedData.newCustomers && extractedData.newCustomers.length > 0 && (
                  <Card title="New Customer Will Be Added" size="small" style={{ marginTop: 16 }}>
                    {extractedData.newCustomers.map((customer, index) => (
                      <div key={index} style={{ marginBottom: 16 }}>
                        <p><strong>Name:</strong> {customer.name}</p>
                        <p><strong>Phone:</strong> {customer.phone || 'Not provided'}</p>
                        <p><strong>Address:</strong> {customer.address || 'Not provided'}</p>
                        {!customerAdded && (
                          <Button
                            type="primary"
                            size="small"
                            loading={addingCustomer}
                            onClick={handleAddCustomer}
                            style={{ marginTop: 8 }}
                          >
                            Add Customer
                          </Button>
                        )}
                      </div>
                    ))}
                  </Card>
                )}

                {extractedData.newProducts && extractedData.newProducts.length > 0 && (
                  <Card title={`${extractedData.newProducts.length} New Products Will Be Added`} size="small" style={{ marginTop: 16 }}>
                    <List
                      size="small"
                      dataSource={extractedData.newProducts}
                      renderItem={(product, index) => (
                        <List.Item>
                          <div style={{ width: '100%' }}>
                            <Text strong>{product.title || product.name || `Product ${index + 1}`}</Text>
                            <br />
                            <Text>ISBN: {product.isbn || 'N/A'} | 
                            Author: {product.author || 'N/A'} | 
                            Publisher: {product.publisher || 'N/A'}</Text>
                            <br />
                            <Text>Qty: {product.quantity || 'N/A'} | 
                            Price: ₹{product.price || 'N/A'} | 
                            Discount: {product.discount || '0'}%</Text>
                          </div>
                        </List.Item>
                      )}
                    />
                    {!productsAdded && (
                      <Button
                        type="primary"
                        size="small"
                        loading={addingProducts}
                        onClick={handleAddProducts}
                        style={{ marginTop: 8 }}
                      >
                        Add All Products
                      </Button>
                    )}
                  </Card>
                )}

                {extractedData.existingProducts && extractedData.existingProducts.length > 0 && (
                  <Card title={`${extractedData.existingProducts.length} Existing Products Found`} size="small" style={{ marginTop: 16 }}>
                    <List
                      size="small"
                      dataSource={extractedData.existingProducts}
                      renderItem={(product, index) => (
                        <List.Item>
                          <div style={{ width: '100%' }}>
                            <Text strong>{product.name || `Product ${index + 1}`}</Text>
                            <br />
                            <Text>ISBN: {product.isbn || 'N/A'} | 
                            Author: {product.author || 'N/A'} | 
                            Publisher: {product.book_publisher?.name || 'N/A'}</Text>
                            <br />
                            <Text>Qty: {product.extractedQuantity || 'N/A'} | 
                            Price: ₹{product.extractedPrice || 'N/A'} | 
                            Discount: {product.extractedDiscount || '0'}%</Text>
                          </div>
                        </List.Item>
                      )}
                    />
                  </Card>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              {!isEditing && !analysisComplete && (
                <Button onClick={handleRetry} style={{ marginRight: 8 }}>
                  Retry
                </Button>
              )}
              {!isEditing && !analysisComplete && (
                <Button type="primary" onClick={handleProceedToAnalysis}>
                  Proceed to Analysis
                </Button>
              )}
              {analysisComplete && (
                <Button type="primary" size="large" onClick={handleUseExtractedData}>
                  Use This Data to Pre-fill Sales Order
                </Button>
              )}
            </div>
          </>
        )}

        <Modal
          open={previewVisible}
          title="Image Preview"
          footer={null}
          onCancel={() => setPreviewVisible(false)}
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    </Modal>
  );
};

export default CustomerPOUpload;
